import { Component, Fragment, Prop, Host, h, Event, EventEmitter, Watch, State } from '@stencil/core';

@Component({
  tag: 'spw-pagination',
  styleUrl: 'spw-pagination.scss',
  shadow: true,
})
export class SpwPagination {
  /** Nombre total d'éléments à paginer */
  @Prop() totalItems: number;
  /** Nombre d'éléments à afficher par page */
  @Prop() itemsPerPage: number;
  /** Indique si la pagination est désactivée */
  @Prop() isDisabled: boolean = false;
  /** Type de variante de la pagination : 'numbers' ou 'input' */
  @Prop() variant: 'numbers' | 'input' = 'numbers';
  /** Page actuellement sélectionnée, mutable */
  @Prop({ mutable: true }) currentPage: number = 1;

  /** Événement émis lorsque la page est changée, renvoie le numéro de la nouvelle page */
  @Event() pageChanged: EventEmitter<number>;

  private totalPages: number;
  @State() inputPageValue: string = '';
  private debounceTimer: any;

  componentWillLoad() {
    this.calculateTotalPages();
    this.inputPageValue = this.currentPage.toString();
  }

  @Watch('totalItems')
  @Watch('itemsPerPage')
  @Watch('currentPage')
  calculateTotalPages() {
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
  }

  private goToPage(page: number) {
    if (page !== this.currentPage && !this.isDisabled && page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.inputPageValue = this.currentPage.toString();
      this.pageChanged.emit(this.currentPage);
    }
  }

  private handleInputChange(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.inputPageValue = inputValue;
    const page = parseInt(inputValue, 10);

    if (!isNaN(page) && page >= 1 && page <= this.totalPages) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.goToPage(page);
      }, 500);
    }
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const page = parseInt(this.inputPageValue, 10);
      if (!isNaN(page)) {
        this.goToPage(page);
      }
    }
  }

  private calculateWidth(): string {
    const length = this.inputPageValue.length;
    if (length <= 1) {
      return '3ch';
    }
    return `${length + 1}ch`;
  }

  private calculatePageRange() {
    const pages = [];
    const currentPage = this.currentPage;
    const totalPages = this.totalPages;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  }

  render() {
    const pages = this.calculatePageRange();
    const showPrevArrow = this.currentPage > 1;
    const showNextArrow = this.currentPage < this.totalPages;

    const prevArrow = showPrevArrow && (
      <li class="spw-pagination__item spw-pagination__item--arrow">
        <button onClick={() => this.goToPage(this.currentPage - 1)}>
          <spw-icon icon="fa-chevron-left"></spw-icon> Précédent
        </button>
      </li>
    );

    const nextArrow = showNextArrow && (
      <li class="spw-pagination__item spw-pagination__item--arrow">
        <button onClick={() => this.goToPage(this.currentPage + 1)}>
          Suivant <spw-icon icon="fa-chevron-right"></spw-icon>
        </button>
      </li>
    );

    return (
      <Host>
        <ul class="spw-pagination">
          {this.variant === 'input' && (
            <Fragment>
              {prevArrow}
              <li class="spw-pagination__input">
                <spw-text-field
                  value={this.inputPageValue}
                  type="number"
                  onInput={event => this.handleInputChange(event)}
                  onKeyDown={event => this.handleKeyDown(event)}
                  disabled={this.isDisabled}
                  style={{ width: this.calculateWidth() }}
                ></spw-text-field>
              </li>
              <li class="spw-pagination__total-pages">sur {this.totalPages} pages</li>
              {nextArrow}
            </Fragment>
          )}

          {this.variant === 'numbers' && (
            <Fragment>
              {prevArrow}

              {pages.map(page => {
                if (page === '...') {
                  return (
                    <li class="spw-pagination__item spw-pagination__item--dots">
                      <spw-icon icon="fa-ellipsis"></spw-icon>
                    </li>
                  );
                }

                return (
                  <li
                    class={{
                      'spw-pagination__item': true,
                      'spw-pagination__item--active': page === this.currentPage,
                    }}
                  >
                    <button onClick={() => this.goToPage(page as number)} disabled={page === this.currentPage}>
                      {page}
                    </button>
                  </li>
                );
              })}

              {nextArrow}
            </Fragment>
          )}
        </ul>
      </Host>
    );
  }
}
